import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Play, Clock, Star, Wallet, AlertTriangle } from 'lucide-react';
import { 
  walletService, 
  chapterService, 
  locationService 
} from '../services/firebase';
import { UserWallet, UserLocation } from '../types/firebase';
import Navbar from './Navbar';
import Footer from './Footer';
import './ChapterList.css';

interface Chapter {
  id: number;
  title: string;
  readTime: string;
  isFree: boolean;
  description: string;
}

type ChapterStatus = 'free' | 'locked' | 'unlocked';

interface ChapterListProps {
  className?: string;
}

const ChapterList: React.FC<ChapterListProps> = ({ className = '' }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [unlockedChapters, setUnlockedChapters] = useState<number[]>([1, 2, 3, 4]);
  const [wallet, setWallet] = useState<UserWallet | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [chapterPrice, setChapterPrice] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [purchaseModalChapter, setPurchaseModalChapter] = useState<Chapter | null>(null);

  // Chapter data
  const chapters: Chapter[] = [
    { id: 1, title: "The Shattered Moon", readTime: "15 min", isFree: true, description: "In the ghostly city of Caer Requiem, Liora awakens to whispers that aren't there..." },
    { id: 2, title: "Echoes of Memory", readTime: "18 min", isFree: true, description: "Three days since the vision, Liora discovers disturbing truths about her past..." },
    { id: 3, title: "The Last Keeper", readTime: "20 min", isFree: true, description: "Erasmus reveals the ancient prophecy and Liora's true destiny..." },
    { id: 4, title: "Bridge Between Worlds", readTime: "16 min", isFree: true, description: "The portal opens and Liora takes her first steps into Lunaria..." },
    { id: 5, title: "Crystal Cities", readTime: "22 min", isFree: false, description: "Liora explores the magnificent realm of Lunaria and meets its guardians..." },
    { id: 6, title: "The Council of Stars", readTime: "19 min", isFree: false, description: "Ancient beings hold court to judge Liora's worthiness..." },
    { id: 7, title: "Shadows of the Past", readTime: "21 min", isFree: false, description: "Dark secrets about the Shattering are revealed..." },
    { id: 8, title: "The Price of Power", readTime: "17 min", isFree: false, description: "Liora must choose between her old life and her destiny..." },
    { id: 9, title: "War of Two Worlds", readTime: "24 min", isFree: false, description: "The conflict between Earth and Lunaria reaches its peak..." },
    { id: 10, title: "The Healing Light", readTime: "20 min", isFree: false, description: "Liora attempts to restore the broken connection..." },
    { id: 11, title: "Convergence", readTime: "25 min", isFree: false, description: "The worlds begin to merge as the final battle approaches..." },
    { id: 12, title: "Echoes Eternal", readTime: "23 min", isFree: false, description: "The epic conclusion to Liora's journey..." }
  ];

  useEffect(() => {
    if (!authLoading && user) {
      loadUserData();
    }
  }, [authLoading, user]);

  const loadUserData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);

      // Load user location
      const locationData = await locationService.getUserLocation();
      setUserLocation(locationData);

      // Load chapter price based on location
      const price = locationService.getChapterPrice(locationData.countryCode);
      setChapterPrice(price);

      // Load wallet
      const walletData = await walletService.getOrCreateWallet(user.uid);
      setWallet(walletData);

      // SECURE: Load purchased chapters from server
      const purchasedChapters = await chapterService.getPurchasedChapters(user.uid);
      
      // Update unlocked chapters (free chapters + purchased chapters)
      const allUnlocked = [...[1, 2, 3, 4], ...purchasedChapters];
      const uniqueUnlocked = Array.from(new Set(allUnlocked));
      setUnlockedChapters(uniqueUnlocked);

    } catch (error) {
      console.error('Error loading user data:', error);
      // For security, don't fallback to localStorage for purchased chapters
      setUnlockedChapters([1, 2, 3, 4]); // Only free chapters
    } finally {
      setLoading(false);
    }
  };

  const handleChapterClick = async (chapter: Chapter): Promise<void> => {
    if (!user) {
      alert('Please sign in to read chapters');
      return;
    }

    // SECURE: Always verify access from server before navigation
    const accessVerification = await chapterService.verifyChapterAccess(user.uid, chapter.id);
    
    if (accessVerification.hasAccess) {
      navigate(`/chapter/${chapter.id}`);
    } else {
      if (chapter.isFree) {
        // Free chapters should always be accessible
        navigate(`/chapter/${chapter.id}`);
      } else {
        handlePurchase(chapter);
      }
    }
  };

  const handlePurchase = (chapter: Chapter): void => {
    if (!user) {
      alert('Please sign in to purchase chapters');
      return;
    }

    if (!wallet) {
      alert('Loading wallet information...');
      return;
    }

    if (wallet.balance < chapterPrice) {
      const confirmed = window.confirm(
        `Insufficient vos balance. You have ${wallet.balance.toFixed(2)} vos but need ${chapterPrice} vos. Would you like to go to your wallet to purchase more vos?`
      );
      if (confirmed) {
        navigate('/wallet');
      }
      return;
    }

    setPurchaseModalChapter(chapter);
  };

  const confirmPurchase = async () => {
    if (!purchaseModalChapter || !user || !userLocation) return;

    try {
      setLoading(true);
      
      // SECURE: Purchase chapter with server-side verification
      const purchaseResult = await chapterService.purchaseChapter(
        user.uid, 
        purchaseModalChapter.id, 
        userLocation.countryCode
      );

      if (purchaseResult.success) {
        // Update unlocked chapters
        const newUnlocked = [...unlockedChapters, purchaseModalChapter.id];
        setUnlockedChapters(newUnlocked);
        
        // Refresh wallet balance from server
        const updatedWallet = await walletService.getOrCreateWallet(user.uid);
        setWallet(updatedWallet);

        alert(`Successfully unlocked Chapter ${purchaseModalChapter.id}!`);
        setPurchaseModalChapter(null);
        
        // Navigate to the chapter
        navigate(`/chapter/${purchaseModalChapter.id}`);
      } else {
        alert(purchaseResult.message || 'Purchase failed. Please try again.');
      }
    } catch (error) {
      console.error('Error purchasing chapter:', error);
      alert('Purchase failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getChapterStatus = (chapter: Chapter): ChapterStatus => {
    if (chapter.isFree) return 'free';
    if (unlockedChapters.includes(chapter.id)) return 'unlocked';
    return 'locked';
  };

  const getChapterPrice = (): string => {
    if (!userLocation || chapterPrice === 0) return '...';
    return `${chapterPrice} vos`;
  };

  const getChapterPriceBadge = (): string => {
    if (!userLocation || chapterPrice === 0) return '...';
    return `${chapterPrice} vos`;
  };

  return (
    <div className={`chapter-list-page ${className}`}>
      <Navbar />
      
      <div className="chapter-hero">
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Echoes Of Lunaria
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Choose your chapter and dive into the mystical world where broken moons hold ancient secrets
          </motion.p>
        </div>
      </div>

      <div className="chapters-container">
        <div className="volume-header">
          <h2>Volume 1</h2>
        </div>
        <div className="chapters-grid">
          {chapters.map((chapter: Chapter, index: number) => {
            const status: ChapterStatus = getChapterStatus(chapter);
            
            return (
              <motion.div
                key={chapter.id}
                className={`chapter-card ${status}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                onClick={() => handleChapterClick(chapter)}
              >
                <div className="chapter-number">
                  {chapter.id}
                </div>
                
                <div className="chapter-content">
                  <div className="chapter-main-content">
                    <div className="chapter-header">
                      <h3>{chapter.title}</h3>
                      <div className="chapter-meta">
                        <span className="read-time">
                          <Clock size={14} />
                          {chapter.readTime}
                        </span>
                        {status === 'free' && (
                          <span className="free-badge">FREE</span>
                        )}
                        {status === 'locked' && (
                          <span className="price-badge">{getChapterPriceBadge()}</span>
                        )}
                      </div>
                    </div>
                    
                    <p className="chapter-description">
                      {chapter.description}
                    </p>
                  </div>
                  
                  <div className="chapter-actions">
                    {status === 'free' || status === 'unlocked' ? (
                      <div className="read-button">
                        <Play size={16} />
                        {status === 'unlocked' ? 'Read Chapter' : 'Read Now'}
                      </div>
                    ) : (
                      <div className="unlock-button">
                        <Lock size={16} />
                        Unlock Chapter
                      </div>
                    )}
                  </div>
                </div>

                {status === 'unlocked' && (
                  <div className="unlocked-indicator">
                    <Star size={16} fill="currentColor" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

      {/* Wallet Balance Display */}
      {user && wallet && userLocation && (
        <div className="wallet-section">
          <div className="wallet-card">
            <h3>
              <Wallet size={20} />
              Your Wallet
            </h3>
            <div className="wallet-info">
              <div className="balance-info">
                <span className="balance-amount">{wallet.balance.toFixed(2)} vos</span>
                <span className="balance-label">Available Balance</span>
              </div>
              <div className="location-info">
                <span className="location-text">{userLocation.country}</span>
                <span className="chapter-price-text">Chapter Price: {chapterPrice} vos</span>
              </div>
              <button 
                className="add-vos-button"
                onClick={() => navigate('/wallet')}
              >
                Add More Vos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Confirmation Modal */}
      {purchaseModalChapter && (
        <div className="modal-overlay" onClick={() => setPurchaseModalChapter(null)}>
          <div className="purchase-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Chapter Purchase</h3>
            <div className="modal-content">
              <div className="chapter-info">
                <h4>Chapter {purchaseModalChapter.id}: {purchaseModalChapter.title}</h4>
                <p>{purchaseModalChapter.description}</p>
              </div>
              
              <div className="purchase-details">
                <div className="detail-row">
                  <span>Price:</span>
                  <span>{chapterPrice} vos</span>
                </div>
                <div className="detail-row">
                  <span>Current Balance:</span>
                  <span>{wallet?.balance.toFixed(2)} vos</span>
                </div>
                <div className="detail-row">
                  <span>After Purchase:</span>
                  <span>{((wallet?.balance || 0) - chapterPrice).toFixed(2)} vos</span>
                </div>
              </div>
              
              <div className="modal-actions">
                <button
                  className="cancel-button"
                  onClick={() => setPurchaseModalChapter(null)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="confirm-button"
                  onClick={confirmPurchase}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Confirm Purchase'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="pricing-info">
        <div className="pricing-card">
          <h3>Chapter Pricing</h3>
          <p>Purchase chapters using vos from your wallet</p>
          <div className="price-display">
            {chapterPrice > 0 ? `${chapterPrice} vos` : '...'} per chapter
          </div>
          {user ? (
            <button 
              className="wallet-button"
              onClick={() => navigate('/wallet')}
            >
              <Wallet size={16} />
              Manage Wallet
            </button>
          ) : (
            <small>Sign in to purchase chapters with vos</small>
          )}
        </div>
      </div>
      </div>

      <Footer />
    </div>
  );
};

export default ChapterList;